import { notification } from "antd";
import history from "./history";
import axios from "axios";
import qs from "qs";

import { actions } from "../store/authRedux/actions";
import { GetUserByRefresh, LogoutBlacklist } from "services/auth";

let store;
const URLBlacklist = ["/account/auth"];
const ENTRY_ROUTE = "/auth";

let isRefreshing = false;
let refreshQueue = [];

export const setUpInterceptorStore = (_store) => {
  store = _store;
};

const normalizePersian = (value) => {
  if (typeof value !== "string") return value;

  return value.replace(/ي/g, "ی").replace(/ك/g, "ک");
};

const normalizeFormData = (formData) => {
  const newFormData = new FormData();

  formData.forEach((value, key) => {
    const normalizedKey = normalizePersian(key);

    if (typeof value === "string") {
      newFormData.append(normalizedKey, normalizePersian(value));
    } else {
      newFormData.append(normalizedKey, value);
    }
  });

  return newFormData;
};

const deepNormalize = (obj) => {
  if (!obj) return obj;

  if (obj instanceof FormData) {
    return normalizeFormData(obj);
  }

  if (typeof obj === "string") {
    return normalizePersian(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(deepNormalize);
  }

  if (typeof obj === "object") {
    const normalized = {};
    Object.keys(obj).forEach((key) => {
      const newKey = normalizePersian(key);
      normalized[newKey] = deepNormalize(obj[key]);
    });
    return normalized;
  }

  return obj;
};

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: (params) => {
    return qs.stringify(params, {
      arrayFormat: "repeat",
    });
  },
});

// API Request interceptor
API.interceptors.request.use(
  (config) => {
    const access_token = store.getState().auth.access;

    const isBlacklisted = URLBlacklist.find((url) => config.url.includes(url));

    if (isBlacklisted) {
      delete config.headers["Authorization"];
    } else if (access_token) {
      config.headers["Authorization"] = `Bearer ${access_token}`;
      config.headers[`Accept-Language`] = "fa";
    } else {
      history.push(ENTRY_ROUTE);
    }

    if (config.params) {
      config.params = deepNormalize(config.params);
    }

    if (config.data) {
      config.data = deepNormalize(config.data);
    }

    return config;
  },
  (error) => {
    notification.error({
      message: "Error",
    });
    Promise.reject(error);
  },
);

// API respone interceptor
API.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (!error.response) {
      notification.error({
        message: "خطا در اتصال به اینترنت",
      });
    }

    // Remove token and redirect
    else if (error.response.status === 401) {
      const isBlacklisted = URLBlacklist.find((url) => error.config.url.includes(url));
      if (isBlacklisted) {
        return Promise.reject(error);
      }

      const refreshToken = store.getState().auth.refresh;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          error.config.headers["Authorization"] = `Bearer ${token}`;
          return API.request(error.config);
        });
      }

      isRefreshing = true;

      return axios
        .post(
          `${process.env.REACT_APP_API_URL}/account/auth/refresh/`,
          { refresh: refreshToken },
          { headers: { "Content-Type": "application/json" } },
        )
        .then((res) => {
          const newToken = res.data?.access || res.access;
          store.dispatch(actions.refresh(newToken));
          refreshQueue.forEach(({ resolve }) => resolve(newToken));
          refreshQueue = [];
          isRefreshing = false;
          error.config.headers["Authorization"] = `Bearer ${newToken}`;
          return API.request(error.config);
        })
        .catch((err) => {
          refreshQueue.forEach(({ reject }) => reject(err));
          refreshQueue = [];
          isRefreshing = false;
          axios
            .post(
              `${process.env.REACT_APP_API_URL}/account/auth/logout/`,
              { refresh: refreshToken },
              { headers: { "Content-Type": "application/json" } },
            )
            .finally(() => {
              store.dispatch(actions.logout());
              notification.error({
                message: "خطایی در احراز هویت رخ داده است. لطفا دوباره وارد شوید",
              });
            });
          return Promise.reject(err);
        });
    } else if (error.response.status === 404) {
      notification.error({
        message: "اطلاعات مورد نظر یافت نشد",
      });
    } else if (error.response.status === 400 || error.response.status === 402) {
      notification.error({
        message:
          Object.keys(error.response.data)[0] +
          " : " +
          error.response.data[Object.keys(error.response.data)[0]],
      });
    } else if (error.response.status === 429) {
      notification.error({
        message:
          "تعداد درخواست هاش ما از حد مجاز گذشته است لطفا بعد از یک دقیقه دوباره تلاش کنید",
      });
    } else if (error.response.status === 409) {
      notification.error({
        message:
          error.response?.data?.message ||
          "خطا در انجام عملیات، لطفا دوباره تلاش کنید.",
      });
    } else if (error.response.status === 500) {
      notification.error({
        message: "خطایی در سرور رخ داده است! لطفا بعدا تلاش کنید",
      });
    } else if (error.response.status === 508) {
      notification.error({
        message: "خطا در اتصال به سرور",
      });
    }

    return Promise.reject(error);
  },
);

export default API;
